import LegalLayout, {
  LegalSection,
  LegalSubheading,
  LegalList,
} from '../components/LegalLayout';
import { BRAND } from '../lib/brand';

const cancellationFees = [
  { method: 'PayPal', fee: '10%' },
  { method: 'Stripe', fee: '5%' },
  { method: 'Airwallex', fee: '5%' },
  { method: 'SumUp', fee: '5%' },
  { method: 'Square', fee: '5%' },
  { method: 'Bank Transfer', fee: '2%' },
];

const refundTimelines = [
  { method: 'PayPal', timeline: '3–5 business days' },
  { method: 'Stripe', timeline: '5–7 business days' },
  { method: 'Airwallex', timeline: '5–7 business days' },
  { method: 'SumUp', timeline: '5–7 business days' },
  { method: 'Square', timeline: '5–7 business days' },
  { method: 'Bank Transfer', timeline: '5–10 business days' },
];

function Table({
  headers,
  rows,
}: {
  headers: [string, string];
  rows: { left: string; right: string }[];
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="min-w-full text-sm">
        <thead className="bg-card text-foreground/90">
          <tr>
            <th className="px-5 py-3 text-left font-semibold">{headers[0]}</th>
            <th className="px-5 py-3 text-left font-semibold">{headers[1]}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-foreground/75">
          {rows.map((r) => (
            <tr key={r.left}>
              <td className="px-5 py-3">{r.left}</td>
              <td className="px-5 py-3">{r.right}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Cancellation() {
  return (
    <LegalLayout
      title="Cancellation & Refund Policy"
      intro={`This policy explains how to cancel a service or subscription with ${BRAND.legalName} and the conditions under which we issue refunds. Please read it together with our Terms of Service.`}
    >
      <LegalSection heading="1. Cancellation">
        <LegalSubheading>1.1 One-time services</LegalSubheading>
        <p>
          To cancel a one-time service, contact{' '}
          <a
            className="text-foreground underline underline-offset-4 hover:opacity-80"
            href={`mailto:${BRAND.email}`}
          >
            {BRAND.email}
          </a>{' '}
          at least seven (7) days before the scheduled start date. Cancellations
          made within seven (7) days of the start date may be subject to the
          cancellation fees described in Section 3.
        </p>

        <LegalSubheading>1.2 Subscription plans</LegalSubheading>
        <p>
          You may cancel a subscription at any time by contacting{' '}
          <a
            className="text-foreground underline underline-offset-4 hover:opacity-80"
            href={`mailto:${BRAND.email}`}
          >
            {BRAND.email}
          </a>
          . Cancellation stops future renewals; it does not entitle you to a
          refund for the unused portion of the current billing cycle, except as
          set out in Section 2.
        </p>
      </LegalSection>

      <LegalSection heading="2. Refunds">
        <LegalSubheading>2.1 One-time services</LegalSubheading>
        <LegalList
          items={[
            'A full refund is available if you cancel seven (7) or more days before the scheduled start date.',
            'For cancellations within seven (7) days of the start date, refunds are issued at our discretion based on work already performed.',
          ]}
        />

        <LegalSubheading>2.2 Customized work</LegalSubheading>
        <p>
          Bespoke deliverables — for example, custom creative, copy, or
          configuration work tailored to your account — are non-refundable once
          work has commenced, because they cannot be resold or reused.
        </p>

        <LegalSubheading>2.3 Subscription plans</LegalSubheading>
        <LegalList
          items={[
            'Refund requests must be submitted within seven (7) days of the initial purchase or renewal.',
            'Refunds for subscription plans are pro-rated to reflect the portion of the billing period that has elapsed and the work delivered.',
          ]}
        />

        <LegalSubheading>2.4 Exceptional circumstances</LegalSubheading>
        <p>
          {BRAND.legalName} reserves the right to grant refunds outside this
          policy for technical failures, prolonged service unavailability, or
          other situations we determine warrant an exception.
        </p>
      </LegalSection>

      <LegalSection heading="3. Cancellation Fees by Payment Method">
        <p>
          Where a cancellation fee applies, it is deducted from the refund
          before disbursement. Payment-processor fees are non-refundable and
          will be excluded from any refunded amount.
        </p>
        <Table
          headers={['Payment method', 'Cancellation fee (of paid amount)']}
          rows={cancellationFees.map((r) => ({
            left: r.method,
            right: r.fee,
          }))}
        />

        <LegalSubheading>3.1 PayPal</LegalSubheading>
        <p>
          Refunds via PayPal are paid net of the 10% cancellation fee shown
          above. PayPal-specific fees may apply and are not refundable.
        </p>

        <LegalSubheading>3.2 Stripe, Airwallex, SumUp, Square</LegalSubheading>
        <p>
          For card-based processors (credit, debit, and prepaid), the
          processor's transaction fees are non-refundable and will be
          deducted in addition to the cancellation fee.
        </p>

        <LegalSubheading>3.3 Bank transfer</LegalSubheading>
        <p>
          For bank-transfer refunds, the amount returned depends on your
          originating currency and country. Bank charges and intermediary fees
          will be deducted from the refunded amount.
        </p>
      </LegalSection>

      <LegalSection heading="4. Refund Timeline">
        <p>
          Approved refunds are issued to the original payment method. Processing
          times depend on the payment provider:
        </p>
        <Table
          headers={['Payment method', 'Refund timeline']}
          rows={refundTimelines.map((r) => ({
            left: r.method,
            right: r.timeline,
          }))}
        />
        <p>
          Timelines reflect the period after we approve and initiate the refund;
          additional time may be required by your bank or card issuer to post
          the credit.
        </p>
      </LegalSection>

      <LegalSection heading="5. How to Request a Cancellation or Refund">
        <p>
          Email{' '}
          <a
            className="text-foreground underline underline-offset-4 hover:opacity-80"
            href={`mailto:${BRAND.email}`}
          >
            {BRAND.email}
          </a>{' '}
          with the following information:
        </p>
        <LegalList
          items={[
            'Your full name and account email',
            'Invoice number or order ID',
            'Reason for the request',
          ]}
        />
        <p>
          We will acknowledge your request within two (2) business days and
          confirm next steps.
        </p>
      </LegalSection>

      <LegalSection heading="6. Contact Us">
        <div className="rounded-xl border border-border bg-card p-5 text-foreground/85">
          <p>
            <strong className="text-foreground">{BRAND.legalName}</strong>
          </p>
          <p>{BRAND.address}</p>
          <p className="mt-2">
            Email:{' '}
            <a
              className="text-foreground underline underline-offset-4 hover:opacity-80"
              href={`mailto:${BRAND.email}`}
            >
              {BRAND.email}
            </a>
          </p>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
